<template>
    <div>
        <h2>Formulaire de données d'accidentologie</h2>
        <b-row class="mb-3 mt-4">
            <b-col>
                {{accidentologie.libelleEntete}}
            </b-col>
        </b-row>
        
        <b-form @submit="onSubmit" @reset="onReset" v-if="show" v-bind:validated="formSubmitted"
                @keypress.enter.prevent>

            <b-col cols="10">

                <b-row align-h="end" class="mb-3">
                    <b-col cols="3"><span class="typo-orange">* </span>{{libelles.champsObligatoires}}</b-col>
                </b-row>
                <b-row align-h="between">
                    <b-col cols="4" >
                        <b-form-group id="input-group-marches" v-if="isAdmin"
                                      :label=libelles.marche label-for="input-marches"
                                      label-class="typo-required-field">
                            <AutocompleteCustom
                                    id="input-marches"
                                    :items="marcheIdsItems"
                                    v-model="marcheAdmin"
                                    @search="onMarcheSearch"
                                    @input="getPreviousMonths"
                                    :required="true"
                                    :disabled="mode === 'UPDATE' || mode === 'READ'"
                                    :placeholder=libelles.selectionnerMarche
                            >
                            </AutocompleteCustom>
                        </b-form-group>
                    </b-col>
                    <b-col cols="6">
                        <div class="user-info">
                            <p style="margin-bottom: 10px; text-align: center;">
                                <strong>Créateur de la saisie</strong>
                            </p>
                            <template v-if="user.nom.trim() || user.prenom.trim() || user.email.trim()">
                                <p><strong>Nom :</strong> {{ user.nom }}</p>
                                <p><strong>Prénom :</strong> {{ user.prenom }}</p>
                                <p><strong>Email :</strong> {{ user.email }}</p>
                            </template>
                            <template v-else>
                                <p class="no-data">La donnée n’est pas disponible</p>
                            </template>
                        </div>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="5">
                        <b-form-group id="input-group-date"
                                      :label=libelles.donneesMois label-for="input-date"
                                      label-class="typo-required-field">
                            <monthly-picker
                                    id="input-date"
                                    v-model="date"
                                    :nb-months-disabled="isAdmin ? false: NB_MONTHS_AVAILABLE"
                                    :clearOption="false"
                                    @change="getPreviousMonths"
                                    :disabled="(mode === 'UPDATE' && !isAdminSecurite) || mode === 'READ'"
                                    v-b-modal="'date-modal'">
                            </monthly-picker>
                            <div v-if="date && !isBeforeCurrentDate()">
                                <b-modal id="date-modal"
                                         title="Confirmation choix de la date"
                                         ok-title="Confirmer"
                                         cancel-title="Annuler"
                                         @cancel="date=null">La date est postérieur à la date actuelle. voulez-vous continuer ?</b-modal>
                            </div>
                            <div class="typo-warning" v-if="!isDateFilled">
                                {{libelles.champObligatoire}}
                            </div>
                        </b-form-group>
                    </b-col>
                    <b-col cols="6" offset="1" v-if="this.marcheAdmin && date && mode === 'CREATE'">
                        <b-row>
                          {{libelles.libelleRienDeclarer}}
                        </b-row>
                        <b-row class="mt-2">
                            <b-button type="button" variant="primary" v-b-modal="'modal-rienADeclarer'">
                                {{libelles.buttonRienDeclarer}}
                            </b-button>
                            <b-modal
                                    :id="'modal-rienADeclarer'"
                                    :title=libelles.demandeConfirmation
                                    ok-title="Confirmer"
                                    cancel-title="Annuler"
                                    @ok="saveReport()">
                                <p>{{libelles.confirmationDispenseUtilisateur}}</p>
                            </b-modal>
                        </b-row>
                    </b-col>
                </b-row>

                <b-card header="Effectifs & nombre d'heures travaillées" class="mb-2">
                    <table>
                        <caption></caption>
                        <thead class="text-center">
                        <tr>
                            <th scope="col"></th>
                            <th scope="col" colspan="2">EFFECTIFS</th>
                            <th scope="col" colspan="2">NOMBRE HEURES TRAVAILLÉES</th>
                        </tr>
                        <tr>
                            <th scope="col"></th>
                            <th scope="col">{{accidentologie.libelleMois}}</th>
                            <th scope="col" v-b-tooltip.hover
                                :title="'L’effectif cumulé sur 12 mois glissants correspond au nombre de personnes différentes ayant intervenu sur le chantier au cours des 12 derniers mois'">
                                {{accidentologie.libelle12MoisGlissant}}
                            </th>
                            <th scope="col">{{accidentologie.libelleMois}}</th>
                            <th scope="col" v-b-tooltip.hover
                                :title=accidentologie.infoBulle12moisGlissant>{{accidentologie.libelle12MoisGlissant}}
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td v-b-tooltip.hover.top
                                :title=accidentologie.infoBullePersonnel>
                                {{accidentologie.libellePersonnelHorsEncadrement}}
                            </td>
                            <td>
                                <b-form-input
                                        id="input-effectifPersonnelGroupement"
                                        v-model="securiteChantiers.effectifPersonnelGroupement"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :state="effectifPersonnelGroupementState"
                                        @change="update12m($event, 'effectifPersonnelGroupement')"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-effectifPersonnelGroupement12m"
                                        v-model="securiteChantiers.effectifPersonnelGroupement12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :state="effectifPersonnelGroupement12mState"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbHeuresTravPersonnelGroupement"
                                        v-model="securiteChantiers.nbHeuresTravPersonnelGroupement"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :state="nbHeuresTravPersonnelGroupementState"
                                        @change="update12m($event, 'nbHeuresTravPersonnelGroupement')"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbHeuresTravPersonnelGroupement12m"
                                        v-model="securiteChantiers.nbHeuresTravPersonnelGroupement12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        @change="updateFields"
                                        :disabled="true"></b-form-input>
                            </td>
                        </tr>
                        <tr>
                            <td>{{accidentologie.libelleInterimaires}}</td>
                            <td>
                                <b-form-input
                                        id="input-effectifInterimaires"
                                        v-model="securiteChantiers.effectifInterimaires"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :state="effectifInterimairesState"
                                        @change="update12m($event, 'effectifInterimaires')"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-effectifInterimaires12m"
                                        v-model="securiteChantiers.effectifInterimaires12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :state="effectifInterimaires12mState"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbHeuresTravInterimaires"
                                        v-model="securiteChantiers.nbHeuresTravInterimaires"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :state="nbHeuresTravInterimairesState"
                                        @change="update12m($event, 'nbHeuresTravInterimaires')"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbHeuresTravInterimaires12m"
                                        v-model="securiteChantiers.nbHeuresTravInterimaires12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        @change="updateFields"
                                        :disabled="true"></b-form-input>
                            </td>
                        </tr>
                        <tr>
                            <td>{{accidentologie.libelleSousTraitants}}</td>
                            <td>
                                <b-form-input
                                        id="input-effectifSousTraitants"
                                        v-model="securiteChantiers.effectifSousTraitants"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :state="effectifSousTraitantsState"
                                        @change="update12m($event, 'effectifSousTraitants')"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-effectifSousTraitants12m"
                                        v-model="securiteChantiers.effectifSousTraitants12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :state="effectifSousTraitants12mState"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbHeuresTravSousTraitants"
                                        v-model="securiteChantiers.nbHeuresTravSousTraitants"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :state="nbHeuresTravSousTraitantsState"
                                        @change="update12m($event, 'nbHeuresTravSousTraitants')"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbHeuresTravSousTraitants12m"
                                        v-model="securiteChantiers.nbHeuresTravSousTraitants12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        @change="updateFields"
                                        :disabled="true"></b-form-input>
                            </td>
                        </tr>
                        <tr>
                            <td>{{accidentologie.libelleGlobalChantierHorsEncadrement}}</td>
                            <td>
                                <b-form-input
                                        id="input-effectifGlobalChantier"
                                        v-model="securiteChantiers.effectifGlobalChantier"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-effectifGlobalChantier12m"
                                        v-model="securiteChantiers.effectifGlobalChantier12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbHeuresTravGlobalChantier"
                                        v-model="securiteChantiers.nbHeuresTravGlobalChantier"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbHeuresTravGlobalChantier12m"
                                        v-model="securiteChantiers.nbHeuresTravGlobalChantier12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :disabled="true"></b-form-input>
                            </td>
                        </tr>
                        <tr>
                            <td v-b-tooltip.hover.top
                                :title=accidentologie.infoBulleEncadrement>{{accidentologie.libelleEncadrement}}
                            </td>
                            <td>
                                <b-form-input
                                        id="input-effectifEncadrementChantier"
                                        v-model="securiteChantiers.effectifEncadrementChantier"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        @change="update12m($event, 'effectifEncadrementChantier')"
                                        :state="effectifEncadrementChantierState"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-effectifEncadrementChantier12m"
                                        v-model="securiteChantiers.effectifEncadrementChantier12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :state="effectifEncadrementChantier12mState"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbHeuresTravEncadrementChantier"
                                        v-model="securiteChantiers.nbHeuresTravEncadrementChantier"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :state="nbHeuresTravEncadrementChantierState"
                                        @change="update12m($event, 'nbHeuresTravEncadrementChantier')"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbHeuresTravEncadrementChantier12m"
                                        v-model="securiteChantiers.nbHeuresTravEncadrementChantier12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        @change="updateFields"
                                        :disabled="true"></b-form-input>
                            </td>
                        </tr>
                        <tr>
                            <td>{{accidentologie.libelleGlobalChantier}}</td>
                            <td>
                                <b-form-input
                                        id="input-effectifGlobalChantierEncadrement"
                                        v-model="securiteChantiers.effectifGlobalChantierEncadrement"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-effectifGlobalChantierEncadrement12m"
                                        v-model="securiteChantiers.effectifGlobalChantierEncadrement12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbHeuresTravGlobalChantierEncadrement"
                                        v-model="securiteChantiers.nbHeuresTravGlobalChantierEncadrement"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbHeuresTravGlobalChantierEncadrement12m"
                                        v-model="securiteChantiers.nbHeuresTravGlobalChantierEncadrement12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :disabled="true"></b-form-input>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </b-card>

                <b-card header="Accidents de travail et jours d'arrêt" class="mb-2">
                    <table>
                        <caption></caption>
                        <thead class="text-center">
                        <tr>
                            <th scope="col"></th>
                            <th scope="col" colspan="2">NOMBRE AT SANS ARRÊT</th>
                            <th scope="col" colspan="2">NOMBRE AT AVEC ARRÊT</th>
                            <th scope="col" colspan="2">NOMBRE JOURS ARRÊT</th>
                        </tr>
                        <tr>
                            <th scope="col"></th>
                            <th scope="col">{{accidentologie.libelleMois}}</th>
                            <th scope="col" v-b-tooltip.hover
                                :title=accidentologie.infoBulle12moisGlissant>{{accidentologie.libelle12MoisGlissant}}
                            </th>
                            <th scope="col">{{accidentologie.libelleMois}}</th>
                            <th scope="col" v-b-tooltip.hover
                                :title=accidentologie.infoBulle12moisGlissant>{{accidentologie.libelle12MoisGlissant}}
                            </th>
                            <th scope="col">{{accidentologie.libelleMois}}</th>
                            <th scope="col" v-b-tooltip.hover
                                :title=accidentologie.infoBulle12moisGlissant>{{accidentologie.libelle12MoisGlissant}}
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td v-b-tooltip.hover.top
                                :title=accidentologie.infoBullePersonnel>
                                {{accidentologie.libellePersonnelHorsEncadrement}}
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentSansArretPersonnelGroupement"
                                        v-model="securiteChantiers.nbAccidentSansArretPersonnelGroupement"
                                        type="number"
                                        min="0"
                                        step="1"
                                        :state="nbAccidentSansArretPersonnelGroupementState"
                                        required
                                        @change="update12m($event, 'nbAccidentSansArretPersonnelGroupement')"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentSansArretPersonnelGroupement12m"
                                        v-model="securiteChantiers.nbAccidentSansArretPersonnelGroupement12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        @change="updateFields"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentAvecArretPersonnelGroupement"
                                        v-model="securiteChantiers.nbAccidentAvecArretPersonnelGroupement"
                                        type="number"
                                        min="0"
                                        step="1"
                                        :state="nbAccidentAvecArretPersonnelGroupementState"
                                        required
                                        @change="update12m($event, 'nbAccidentAvecArretPersonnelGroupement')"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentAvecArretPersonnelGroupement12m"
                                        v-model="securiteChantiers.nbAccidentAvecArretPersonnelGroupement12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        @change="updateFields"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbJourArretPersonnelGroupement"
                                        v-model="securiteChantiers.nbJourArretPersonnelGroupement"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        @change="update12m($event, 'nbJourArretPersonnelGroupement')"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbJourArretPersonnelGroupement12m"
                                        v-model="securiteChantiers.nbJourArretPersonnelGroupement12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        @change="updateFields"
                                        :disabled="true"></b-form-input>
                            </td>
                        </tr>
                        <tr>
                            <td>{{accidentologie.libelleInterimaires}}</td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentSansArretInterimaires"
                                        v-model="securiteChantiers.nbAccidentSansArretInterimaires"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :state="nbAccidentSansArretInterimairesState"
                                        @change="update12m($event, 'nbAccidentSansArretInterimaires')"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentSansArretInterimaires12m"
                                        v-model="securiteChantiers.nbAccidentSansArretInterimaires12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        @change="updateFields"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentAvecArretInterimaires"
                                        v-model="securiteChantiers.nbAccidentAvecArretInterimaires"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :state="nbAccidentAvecArretInterimairesState"
                                        @change="update12m($event, 'nbAccidentAvecArretInterimaires')"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentAvecArretInterimaires12m"
                                        v-model="securiteChantiers.nbAccidentAvecArretInterimaires12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        @change="updateFields"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbJourArretInterimaires"
                                        v-model="securiteChantiers.nbJourArretInterimaires"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        @change="update12m($event, 'nbJourArretInterimaires')"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbJourArretInterimaires12m"
                                        v-model="securiteChantiers.nbJourArretInterimaires12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        @change="updateFields"
                                        :disabled="true"></b-form-input>
                            </td>
                        </tr>
                        <tr>
                            <td>{{accidentologie.libelleSousTraitants}}</td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentSansArretSousTraitants"
                                        v-model="securiteChantiers.nbAccidentSansArretSousTraitants"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :state="nbAccidentSansArretSousTraitantsState"
                                        @change="update12m($event, 'nbAccidentSansArretSousTraitants')"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentSansArretSousTraitants12m"
                                        v-model="securiteChantiers.nbAccidentSansArretSousTraitants12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        @change="updateFields"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentAvecArretSousTraitants"
                                        v-model="securiteChantiers.nbAccidentAvecArretSousTraitants"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :state="nbAccidentAvecArretSousTraitantsState"
                                        @change="update12m($event, 'nbAccidentAvecArretSousTraitants')"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentAvecArretSousTraitants12m"
                                        v-model="securiteChantiers.nbAccidentAvecArretSousTraitants12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        @change="updateFields"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbJourArretSousTraitants"
                                        v-model="securiteChantiers.nbJourArretSousTraitants"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        @change="update12m($event, 'nbJourArretSousTraitants')"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbJourArretSousTraitants12m"
                                        v-model="securiteChantiers.nbJourArretSousTraitants12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        @change="updateFields"
                                        :disabled="true"></b-form-input>
                            </td>
                        </tr>
                        <tr>
                            <td>{{accidentologie.libelleGlobalChantierHorsEncadrement}}</td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentSansArretGlobalChantier"
                                        v-model="securiteChantiers.nbAccidentSansArretGlobalChantier"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentSansArretGlobalChantier12m"
                                        v-model="securiteChantiers.nbAccidentSansArretGlobalChantier12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentAvecArretGlobalChantier"
                                        v-model="securiteChantiers.nbAccidentAvecArretGlobalChantier"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentAvecArretGlobalChantier12m"
                                        v-model="securiteChantiers.nbAccidentAvecArretGlobalChantier12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbJourArretGlobalChantier"
                                        v-model="securiteChantiers.nbJourArretGlobalChantier"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbJourArretGlobalChantier12m"
                                        v-model="securiteChantiers.nbJourArretGlobalChantier12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :disabled="true"></b-form-input>
                            </td>
                        </tr>
                        <tr>
                            <td v-b-tooltip.hover.top
                                :title=accidentologie.infoBulleEncadrement>{{accidentologie.libelleEncadrement}}
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentSansArretEncadrementChantier"
                                        v-model="securiteChantiers.nbAccidentSansArretEncadrementChantier"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :state="nbAccidentSansArretEncadrementChantierState"
                                        @change="update12m($event, 'nbAccidentSansArretEncadrementChantier')"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentSansArretEncadrementChantier12m"
                                        v-model="securiteChantiers.nbAccidentSansArretEncadrementChantier12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        @change="updateFields"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentAvecArretEncadrementChantier"
                                        v-model="securiteChantiers.nbAccidentAvecArretEncadrementChantier"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :state="nbAccidentAvecArretEncadrementChantierState"
                                        @change="update12m($event, 'nbAccidentAvecArretEncadrementChantier')"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentAvecArretEncadrementChantier12m"
                                        v-model="securiteChantiers.nbAccidentAvecArretEncadrementChantier12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        @change="updateFields"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbJourArretEncadrementChantier"
                                        v-model="securiteChantiers.nbJourArretEncadrementChantier"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        @change="update12m($event, 'nbJourArretEncadrementChantier')"
                                        :disabled="mode === 'READ'"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbJourArretEncadrementChantier12m"
                                        v-model="securiteChantiers.nbJourArretEncadrementChantier12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        @change="updateFields"
                                        :disabled="true"></b-form-input>
                            </td>
                        </tr>
                        <tr>
                            <td>{{accidentologie.libelleGlobalChantier}}</td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentSansArretGlobalChantierEncadrement"
                                        v-model="securiteChantiers.nbAccidentSansArretGlobalChantierEncadrement"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentSansArretGlobalChantierEncadrement12m"
                                        v-model="securiteChantiers.nbAccidentSansArretGlobalChantierEncadrement12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentAvecArretGlobalChantierEncadrement"
                                        v-model="securiteChantiers.nbAccidentAvecArretGlobalChantierEncadrement"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbAccidentAvecArretGlobalChantierEncadrement12m"
                                        v-model="securiteChantiers.nbAccidentAvecArretGlobalChantierEncadrement12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbJourArretGlobalChantierEncadrement"
                                        v-model="securiteChantiers.nbJourArretGlobalChantierEncadrement"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-nbJourArretGlobalChantierEncadrement12m"
                                        v-model="securiteChantiers.nbJourArretGlobalChantierEncadrement12m"
                                        type="number"
                                        min="0"
                                        step="1"
                                        required
                                        :disabled="true"></b-form-input>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </b-card>

                <b-card header="Taux et indice" class="mb-2">
                    <table>
                        <caption></caption>
                        <thead class="text-center">
                        <tr>
                            <th scope="col"></th>
                            <th scope="col" colspan="2">TAUX DE FRÉQUENCE</th>
                            <th scope="col" colspan="2">INDICE DE FRÉQUENCE</th>
                            <th scope="col" colspan="2">TAUX DE GRAVITÉ</th>
                        </tr>
                        <tr>
                            <th scope="col"></th>
                            <th scope="col">{{accidentologie.libelleMois}}</th>
                            <th scope="col" v-b-tooltip.hover
                                :title=accidentologie.infoBulle12moisGlissant>{{accidentologie.libelle12MoisGlissant}}
                            </th>
                            <th scope="col">{{accidentologie.libelleMois}}</th>
                            <th scope="col" v-b-tooltip.hover
                                :title=accidentologie.infoBulle12moisGlissant>{{accidentologie.libelle12MoisGlissant}}
                            </th>
                            <th scope="col">{{accidentologie.libelleMois}}</th>
                            <th scope="col" v-b-tooltip.hover
                                :title=accidentologie.infoBulle12moisGlissant>{{accidentologie.libelle12MoisGlissant}}
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td v-b-tooltip.hover.top
                                :title=accidentologie.infoBullePersonnel>
                                {{accidentologie.libellePersonnelHorsEncadrement}}
                            </td>
                            <td>
                                <b-form-input
                                        id="input-tauxFrequencePersonnelGroupement"
                                        v-model="securiteChantiers.tauxFrequencePersonnelGroupement"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-tauxFrequencePersonnelGroupement12m"
                                        v-model="securiteChantiers.tauxFrequencePersonnelGroupement12m"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-indiceFrequencePersonnelGroupement"
                                        v-model="securiteChantiers.indiceFrequencePersonnelGroupement"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-indiceFrequencePersonnelGroupement12m"
                                        v-model="securiteChantiers.indiceFrequencePersonnelGroupement12m"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-tauxGravitePersonnelGroupement"
                                        v-model="securiteChantiers.tauxGravitePersonnelGroupement"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-tauxGravitePersonnelGroupement12m"
                                        v-model="securiteChantiers.tauxGravitePersonnelGroupement12m"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                        </tr>
                        <tr>
                            <td>{{accidentologie.libelleInterimaires}}</td>
                            <td>
                                <b-form-input
                                        id="input-tauxFrequenceInterimaires"
                                        v-model="securiteChantiers.tauxFrequenceInterimaires"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-tauxFrequenceInterimaires12m"
                                        v-model="securiteChantiers.tauxFrequenceInterimaires12m"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-indiceFrequenceInterimaires"
                                        v-model="securiteChantiers.indiceFrequenceInterimaires"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-indiceFrequenceInterimaires12m"
                                        v-model="securiteChantiers.indiceFrequenceInterimaires12m"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-tauxGraviteInterimaires"
                                        v-model="securiteChantiers.tauxGraviteInterimaires"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-tauxGraviteInterimaires12m"
                                        v-model="securiteChantiers.tauxGraviteInterimaires12m"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                        </tr>
                        <tr>
                            <td>{{accidentologie.libelleSousTraitants}}</td>
                            <td>
                                <b-form-input
                                        id="input-tauxFrequenceSousTraitants"
                                        v-model="securiteChantiers.tauxFrequenceSousTraitants"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-tauxFrequenceSousTraitants12m"
                                        v-model="securiteChantiers.tauxFrequenceSousTraitants12m"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-indiceFrequenceSousTraitants"
                                        v-model="securiteChantiers.indiceFrequenceSousTraitants"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-indiceFrequenceSousTraitants12m"
                                        v-model="securiteChantiers.indiceFrequenceSousTraitants12m"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-tauxGraviteSousTraitants"
                                        v-model="securiteChantiers.tauxGraviteSousTraitants"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-tauxGraviteSousTraitants12m"
                                        v-model="securiteChantiers.tauxGraviteSousTraitants12m"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                        </tr>
                        <tr>
                            <td>{{accidentologie.libelleGlobalChantierHorsEncadrement}}</td>
                            <td>
                                <b-form-input
                                        id="input-tauxFrequenceGlobalChantier"
                                        v-model="securiteChantiers.tauxFrequenceGlobalChantier"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-tauxFrequenceGlobalChantier12m"
                                        v-model="securiteChantiers.tauxFrequenceGlobalChantier12m"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-indiceFrequenceGlobalChantier"
                                        v-model="securiteChantiers.indiceFrequenceGlobalChantier"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-indiceFrequenceGlobalChantier12m"
                                        v-model="securiteChantiers.indiceFrequenceGlobalChantier12m"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-tauxGraviteGlobalChantier"
                                        v-model="securiteChantiers.tauxGraviteGlobalChantier"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-tauxGraviteGlobalChantier12m"
                                        v-model="securiteChantiers.tauxGraviteGlobalChantier12m"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                        </tr>
                        <tr>
                            <td v-b-tooltip.hover.top
                                :title=accidentologie.infoBulleEncadrement>{{accidentologie.libelleEncadrement}}
                            </td>
                            <td>
                                <b-form-input
                                        id="input-tauxFrequenceEncadrementChantier"
                                        v-model="securiteChantiers.tauxFrequenceEncadrementChantier"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-tauxFrequenceEncadrementChantier12m"
                                        v-model="securiteChantiers.tauxFrequenceEncadrementChantier12m"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-indiceFrequenceEncadrementChantier"
                                        v-model="securiteChantiers.indiceFrequenceEncadrementChantier"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-indiceFrequenceEncadrementChantier12m"
                                        v-model="securiteChantiers.indiceFrequenceEncadrementChantier12m"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-tauxGraviteEncadrementChantier"
                                        v-model="securiteChantiers.tauxGraviteEncadrementChantier"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-tauxGraviteEncadrementChantier12m"
                                        v-model="securiteChantiers.tauxGraviteEncadrementChantier12m"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                        </tr>
                        <tr>
                            <td>{{accidentologie.libelleGlobalChantier}}</td>
                            <td>
                                <b-form-input
                                        id="input-tauxFrequenceGlobalChantierEncadrement"
                                        v-model="securiteChantiers.tauxFrequenceGlobalChantierEncadrement"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-tauxFrequenceGlobalChantierEncadrement12m"
                                        v-model="securiteChantiers.tauxFrequenceGlobalChantierEncadrement12m"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-indiceFrequenceGlobalChantierEncadrement"
                                        v-model="securiteChantiers.indiceFrequenceGlobalChantierEncadrement"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-indiceFrequenceGlobalChantierEncadrement12m"
                                        v-model="securiteChantiers.indiceFrequenceGlobalChantierEncadrement12m"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-tauxGraviteGlobalChantierEncadrement"
                                        v-model="securiteChantiers.tauxGraviteGlobalChantierEncadrement"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                            <td>
                                <b-form-input
                                        id="input-tauxGraviteGlobalChantierEncadrement12m"
                                        v-model="securiteChantiers.tauxGraviteGlobalChantierEncadrement12m"
                                        type="number"
                                        step="any"
                                        :disabled="true"></b-form-input>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </b-card>

                <b-row v-if="mode !== 'CREATE'">
                    <b-col>
                        <b-form-group id="input-group-commentaire" label="Commentaire"
                                      label-for="input-commentaire"
                                      :label-class="securiteChantiers.jourSoumissionPassed ? 'typo-required-field' : ''">
                            <b-form-textarea
                                    id="input-commentaire"
                                    v-model="securiteChantiers.commentaire"
                                    rows="3"
                                    max-rows="5"
                                    :required="securiteChantiers.jourSoumissionPassed"
                                    :disabled="mode === 'READ'"
                            ></b-form-textarea>
                        </b-form-group>
                    </b-col>
                    <b-col cols="6" v-if="securiteChantiers.statut === 'REFUSE'">
                        <b-form-group
                            id="input-group-commentaire-statut"
                            :label=libelles.commentaireStatut
                            label-for="input-commentaire-statut"
                        >
                            <b-form-textarea
                                id="input-commentaire"
                                :value="securiteChantiers.commentaireStatut"
                                rows="3"
                                max-rows="5"
                                disabled
                            ></b-form-textarea>
                        </b-form-group>
                    </b-col>
                </b-row>

                <div class="pt-2" v-if="isMissingMonths">
                  <b-alert show variant="warning">
                    Les mois suivants ne sont pas indiqués: {{missingMonths}}
                  </b-alert>
                </div>

                <div class="pt-2" v-if="invalidForm12M">
                  <b-alert show variant="danger">
                    {{accidentologie.erreur12mMessage}}
                  </b-alert>
                </div>

                 <div class="pt-2" v-if="invalidFormNbHeures">
                  <b-alert show variant="danger">
                    {{accidentologie.erreurNbHeuresMessage}}
                  </b-alert>
                </div>

                <div class="pt-2" v-if="mode !== 'READ'">
                    <b-button type="submit" variant="primary" @click="formSubmitted = true" :disabled="invalidForm12M || invalidFormNbHeures">{{libelles.soumettre}}
                    </b-button>
                    <b-button v-if="mode === 'CREATE'" type="reset" variant="danger">{{libelles.reinitialiser}}
                    </b-button>
                </div>

                <div class="pt-2" v-if="mode === 'READ' && isAdminSecurite">
                    <b-button
                            v-if="securiteChantiers.statut === 'ATTENTE_MODIFICATION' || securiteChantiers.statut === 'ATTENTE_SUPPRESSION'"
                            type="button" variant="primary" @click="validerSecuriteChantiers(securiteChantiers)">
                        <span v-if="securiteChantiers.statut === 'ATTENTE_MODIFICATION'">{{libelles.confirmerModification}}</span>
                        <span v-if="securiteChantiers.statut === 'ATTENTE_SUPPRESSION'">{{libelles.confirmerSuppression}}</span>
                    </b-button>
                </div>

            </b-col>

        </b-form>
        <br/>
       
    </div>

</template>

<script>
  import MarcheSecuriteChantiersDto from "../../dtos/MarcheSecuriteChantiersDto";
  import SessionStore from '@/stores/SessionStore'
  import hasPermission from "../../filters/hasPermission";
  import MarcheStore from "../../stores/MarcheStore";
  import MarcheSecuriteChantiersStore from "../../stores/MarcheSecuriteChantiersStore";
  import MonthlyPicker from '../../components/MonthlyPicker.vue'
  import moment from 'moment';
  import {mois, moisIndexe} from "../../constants/enums";
  import {divide, sum} from "../../utils/mathUtil";
  import {momentToDate} from "../../utils/dateUtil";
  import {accidentologie, libelles} from "../../constants/libelles";
  import AlertStore from "@/stores/AlertStore";
  import {string2literal} from "../../utils/stringUtil";
  import AutocompleteCustom from "../../components/AutocompleteCustom";
  import {EventBus} from '@/event-bus'

  export default {
    components: {MonthlyPicker, AutocompleteCustom},
    data() {
      return {
        NB_MONTHS_AVAILABLE: 7,
        formSubmitted: false,
        show: true,
        sessionStoreState: SessionStore.state,
        securiteChantiers: new MarcheSecuriteChantiersDto(),
        previousSecuriteChantiers: [],
        date: null,
        monthLabels: mois,
        marcheAdmin: null,
        marchesAdmin: [],
        marches: [],
        accidentologie: accidentologie,
        libelles: libelles,
        marcheIdsItems: [],
        isMissingMonths: false,
        missingMonths: '',
        invalidForm12M: false,
        invalidFormNbHeures: false,
      }
    },
    computed: {
      user() {
        if(this.mode === 'CREATE'){
          return {
          email: SessionStore.state.email, 
          prenom: SessionStore.state.prenom, 
          nom: SessionStore.state.nom
          }
        }
          return {
          email: this.securiteChantiers.user?.email || " ", 
          prenom: this.securiteChantiers.user?.prenom || " ", 
          nom: this.securiteChantiers.user?.nom || " "
         }
      },
      mode() {
        return !this.$route.params.id ? 'CREATE' : (this.securiteChantiers.statut === 'VALIDE' && !hasPermission(["CORRESPONDANT_SECURITE_LECTURE"])) ? 'UPDATE' : 'READ';
      },
      isAdminSecurite() {
        return hasPermission(['ADMINISTRATEUR', 'CORRESPONDANT_SECURITE_ADMINISTRATEUR']);
      },
      isAdmin() {
        return this.isAdminSecurite && !this.sessionStoreState.marches.length;
      },
      isDateFilled() {
        return this.formSubmitted ? this.date != null && this.date._isValid : true;
      },

      effectifPersonnelGroupementState(){
        if(this.isValueZeroField("effectifPersonnelGroupement","nbHeuresTravPersonnelGroupement", true)){
          return false;
        }
        return this.check12MField("effectifPersonnelGroupement", "effectifPersonnelGroupement12m");
      },
      effectifPersonnelGroupement12mState(){
        return this.check12MField("effectifPersonnelGroupement", "effectifPersonnelGroupement12m");
      },
      nbHeuresTravPersonnelGroupementState() {
        if(this.isValueZeroField("effectifPersonnelGroupement","nbHeuresTravPersonnelGroupement", true)){
          return false;
        }
        if(this.isValueZeroField("nbAccidentSansArretPersonnelGroupement","nbHeuresTravPersonnelGroupement", false)){
          return false;
        }
        if(this.isValueZeroField("nbAccidentAvecArretPersonnelGroupement","nbHeuresTravPersonnelGroupement", false)){
          return false;
        }
        return null;
      },

      effectifInterimairesState(){
        if(this.isValueZeroField("effectifInterimaires","nbHeuresTravInterimaires", true)){
          return false;
        }
        return this.check12MField("effectifInterimaires", "effectifInterimaires12m");
      },
      effectifInterimaires12mState(){
        return this.check12MField("effectifInterimaires", "effectifInterimaires12m");
      },
      nbHeuresTravInterimairesState() {
        if(this.isValueZeroField("effectifInterimaires","nbHeuresTravInterimaires", true)){
          return false;
        }
        if(this.isValueZeroField("nbAccidentSansArretInterimaires","nbHeuresTravInterimaires", false)){
          return false;
        }
        if(this.isValueZeroField("nbAccidentAvecArretInterimaires","nbHeuresTravInterimaires", false)){
          return false;
        }
        return null;
      },

      effectifSousTraitantsState(){
        if(this.isValueZeroField("effectifSousTraitants","nbHeuresTravSousTraitants", true)){
          return false;
        }
        return this.check12MField("effectifSousTraitants", "effectifSousTraitants12m");
      },
      effectifSousTraitants12mState(){
        return this.check12MField("effectifSousTraitants", "effectifSousTraitants12m");
      },
      nbHeuresTravSousTraitantsState() {
        if(this.isValueZeroField("effectifSousTraitants","nbHeuresTravSousTraitants", true)){
          return false;
        }
        if(this.isValueZeroField("nbAccidentSansArretSousTraitants","nbHeuresTravSousTraitants", false)){
          return false;
        }
        if(this.isValueZeroField("nbAccidentAvecArretSousTraitants","nbHeuresTravSousTraitants", false)){
          return false;
        }
        return null;
      },

      effectifEncadrementChantierState(){
        if(this.isValueZeroField("effectifEncadrementChantier","nbHeuresTravEncadrementChantier", true)){
          return false;
        }
        return this.check12MField("effectifEncadrementChantier", "effectifEncadrementChantier12m");
      },
      effectifEncadrementChantier12mState(){
        return this.check12MField("effectifEncadrementChantier", "effectifEncadrementChantier12m");
      },
      nbHeuresTravEncadrementChantierState() {
        if(this.isValueZeroField("effectifEncadrementChantier","nbHeuresTravEncadrementChantier", true)){
          return false;
        }
        if(this.isValueZeroField("nbAccidentSansArretEncadrementChantier","nbHeuresTravEncadrementChantier", false)){
          return false;
        }
        if(this.isValueZeroField("nbAccidentAvecArretEncadrementChantier","nbHeuresTravEncadrementChantier", false)){
          return false;
        }
        return null;
      },

      nbAccidentSansArretPersonnelGroupementState(){
        if(this.isValueZeroField("nbAccidentSansArretPersonnelGroupement","nbHeuresTravPersonnelGroupement", false)){
          return false;
        }
        return null;
      },
      nbAccidentAvecArretPersonnelGroupementState(){
        if(this.isValueZeroField("nbAccidentAvecArretPersonnelGroupement","nbHeuresTravPersonnelGroupement", false)){
          return false;
        }
        return null;
      },

      nbAccidentSansArretInterimairesState(){
        if(this.isValueZeroField("nbAccidentSansArretInterimaires","nbHeuresTravInterimaires", false)){
          return false;
        }
        return null;
      },
      nbAccidentAvecArretInterimairesState(){
        if(this.isValueZeroField("nbAccidentAvecArretInterimaires","nbHeuresTravInterimaires", false)){
          return false;
        }
        return null;
      },

      nbAccidentSansArretSousTraitantsState(){
        if(this.isValueZeroField("nbAccidentSansArretSousTraitants","nbHeuresTravSousTraitants", false)){
          return false;
        }
        return null;
      },
      nbAccidentAvecArretSousTraitantsState(){
        if(this.isValueZeroField("nbAccidentAvecArretSousTraitants","nbHeuresTravSousTraitants", false)){
          return false;
        }
        return null;
      },

      nbAccidentSansArretEncadrementChantierState(){
        if(this.isValueZeroField("nbAccidentSansArretEncadrementChantier","nbHeuresTravEncadrementChantier", false)){
          return false;
        }
        return null;
      },
      nbAccidentAvecArretEncadrementChantierState(){
        if(this.isValueZeroField("nbAccidentAvecArretEncadrementChantier","nbHeuresTravEncadrementChantier", false)){
          return false;
        }
        return null;
      }
    },
    watch: {
      $route() {
        this.initSecuriteChantiers();
      },
      securiteChantiers: {
        handler: function (newSecuriteChantiers) {
          if (this.mode === 'CREATE') {
            localStorage.securiteChantiers = JSON.stringify(newSecuriteChantiers);
          }
        },
        deep: true,
      },
      date(newDate) {
        if (this.mode === 'CREATE' && newDate && newDate._isValid) {
          localStorage.dateSecuriteChantiers = newDate;
        }
      },
      marcheAdmin(newMarcheAdmin) {
        this.onMarcheSearch(newMarcheAdmin, true);
        if (this.mode === 'CREATE') {
          localStorage.marcheAdminSecuriteChantiers = newMarcheAdmin;
        }
      },
      previousSecuriteChantiers(newPreviousSecuriteChantiers){
        if (this.mode === 'CREATE') {
          localStorage.previousSecuriteChantiers= JSON.stringify(newPreviousSecuriteChantiers);
        }
      }
    },
    mounted() {
      if (this.isAdmin) {
        MarcheStore.getAll().then(marches => {
          this.marches = marches;
          this.marchesAdmin = marches.map(marche => {
            return {text: `${marche.reference} (${marche.libelleCourt})`, value: marche.id};
          });
          this.marcheIdsItems = this.marchesAdmin;
        });
      }
      EventBus.$on("NavBar-marcheChange", () => {
        if (!this.isAdmin) {
          MarcheSecuriteChantiersStore.clearLocalStorage();
          this.initSecuriteChantiers();
        }
      });
      this.initSecuriteChantiers();
    },
    beforeDestroy() {
      EventBus.$off("NavBar-marcheChange");
    },
    methods: {
      initSecuriteChantiers() {
        this.marcheAdmin = null;
        this.date = null;
        this.formSubmitted = false;
        this.show = false;
        this.$nextTick(() => {
          this.show = true
        });
        if (this.mode !== 'CREATE') {
          MarcheSecuriteChantiersStore.get(this.$route.params.id).then(value => {
            if (value) {
              this.securiteChantiers = value;
              this.marcheAdmin = value.marche.id;
              this.date = moment(value.date);
              this.getPreviousMonths();
            }
          });
        } else {
          this.getLocalStorage();
        }
      },
      getPreviousMonths() {
        const marcheId = this.isAdmin ? this.marcheAdmin : this.sessionStoreState.marche.id;
        if (this.mode !== 'READ' && marcheId && this.date && this.date._isValid) {
          if (this.mode === 'CREATE') {
            this.securiteChantiers = new MarcheSecuriteChantiersDto();
          }
          MarcheSecuriteChantiersStore.getPreviousMonths(marcheId, momentToDate(this.date)).then(value => {
            this.previousSecuriteChantiers = value;
          });
        }
      },
      update12m(fieldValue, fieldName) {
        this.securiteChantiers[fieldName + '12m'] = this.previousSecuriteChantiers.reduce((acc, securiteChantiers) => {
          return securiteChantiers[fieldName] != null ? acc + Number(securiteChantiers[fieldName]) : acc;
        }, Number(fieldValue));
        this.updateFields();
      },
      updateFields() {
        this.securiteChantiers.effectifGlobalChantier = this.sum([this.securiteChantiers.effectifPersonnelGroupement, this.securiteChantiers.effectifInterimaires, this.securiteChantiers.effectifSousTraitants]);
        this.securiteChantiers.effectifGlobalChantier12m = this.sum([this.securiteChantiers.effectifPersonnelGroupement12m, this.securiteChantiers.effectifInterimaires12m, this.securiteChantiers.effectifSousTraitants12m]);
        this.securiteChantiers.effectifGlobalChantierEncadrement = this.sum([this.securiteChantiers.effectifGlobalChantier, this.securiteChantiers.effectifEncadrementChantier]);
        this.securiteChantiers.effectifGlobalChantierEncadrement12m = this.sum([this.securiteChantiers.effectifGlobalChantier12m, this.securiteChantiers.effectifEncadrementChantier12m]);
        this.securiteChantiers.nbHeuresTravGlobalChantier = this.sum([this.securiteChantiers.nbHeuresTravPersonnelGroupement, this.securiteChantiers.nbHeuresTravInterimaires, this.securiteChantiers.nbHeuresTravSousTraitants]);
        this.securiteChantiers.nbHeuresTravGlobalChantier12m = this.sum([this.securiteChantiers.nbHeuresTravPersonnelGroupement12m, this.securiteChantiers.nbHeuresTravInterimaires12m, this.securiteChantiers.nbHeuresTravSousTraitants12m]);
        this.securiteChantiers.nbHeuresTravGlobalChantierEncadrement = this.sum([this.securiteChantiers.nbHeuresTravGlobalChantier, this.securiteChantiers.nbHeuresTravEncadrementChantier]);
        this.securiteChantiers.nbHeuresTravGlobalChantierEncadrement12m = this.sum([this.securiteChantiers.nbHeuresTravGlobalChantier12m, this.securiteChantiers.nbHeuresTravEncadrementChantier12m]);

        this.securiteChantiers.nbAccidentSansArretGlobalChantier = this.sum([this.securiteChantiers.nbAccidentSansArretPersonnelGroupement, this.securiteChantiers.nbAccidentSansArretInterimaires, this.securiteChantiers.nbAccidentSansArretSousTraitants]);
        this.securiteChantiers.nbAccidentSansArretGlobalChantier12m = this.sum([this.securiteChantiers.nbAccidentSansArretPersonnelGroupement12m, this.securiteChantiers.nbAccidentSansArretInterimaires12m, this.securiteChantiers.nbAccidentSansArretSousTraitants12m]);
        this.securiteChantiers.nbAccidentSansArretGlobalChantierEncadrement = this.sum([this.securiteChantiers.nbAccidentSansArretGlobalChantier, this.securiteChantiers.nbAccidentSansArretEncadrementChantier]);
        this.securiteChantiers.nbAccidentSansArretGlobalChantierEncadrement12m = this.sum([this.securiteChantiers.nbAccidentSansArretGlobalChantier12m, this.securiteChantiers.nbAccidentSansArretEncadrementChantier12m]);
        this.securiteChantiers.nbAccidentAvecArretGlobalChantier = this.sum([this.securiteChantiers.nbAccidentAvecArretPersonnelGroupement, this.securiteChantiers.nbAccidentAvecArretInterimaires, this.securiteChantiers.nbAccidentAvecArretSousTraitants]);
        this.securiteChantiers.nbAccidentAvecArretGlobalChantier12m = this.sum([this.securiteChantiers.nbAccidentAvecArretPersonnelGroupement12m, this.securiteChantiers.nbAccidentAvecArretInterimaires12m, this.securiteChantiers.nbAccidentAvecArretSousTraitants12m]);
        this.securiteChantiers.nbAccidentAvecArretGlobalChantierEncadrement = this.sum([this.securiteChantiers.nbAccidentAvecArretGlobalChantier, this.securiteChantiers.nbAccidentAvecArretEncadrementChantier]);
        this.securiteChantiers.nbAccidentAvecArretGlobalChantierEncadrement12m = this.sum([this.securiteChantiers.nbAccidentAvecArretGlobalChantier12m, this.securiteChantiers.nbAccidentAvecArretEncadrementChantier12m]);
        this.securiteChantiers.nbJourArretGlobalChantier = this.sum([this.securiteChantiers.nbJourArretPersonnelGroupement, this.securiteChantiers.nbJourArretInterimaires, this.securiteChantiers.nbJourArretSousTraitants]);
        this.securiteChantiers.nbJourArretGlobalChantier12m = this.sum([this.securiteChantiers.nbJourArretPersonnelGroupement12m, this.securiteChantiers.nbJourArretInterimaires12m, this.securiteChantiers.nbJourArretSousTraitants12m]);
        this.securiteChantiers.nbJourArretGlobalChantierEncadrement = this.sum([this.securiteChantiers.nbJourArretGlobalChantier, this.securiteChantiers.nbJourArretEncadrementChantier]);
        this.securiteChantiers.nbJourArretGlobalChantierEncadrement12m = this.sum([this.securiteChantiers.nbJourArretGlobalChantier12m, this.securiteChantiers.nbJourArretEncadrementChantier12m]);

        this.securiteChantiers.tauxFrequencePersonnelGroupement = this.divide(this.securiteChantiers.nbAccidentAvecArretPersonnelGroupement, this.securiteChantiers.nbHeuresTravPersonnelGroupement, 1000000);
        this.securiteChantiers.tauxFrequencePersonnelGroupement12m = this.divide(this.securiteChantiers.nbAccidentAvecArretPersonnelGroupement12m, this.securiteChantiers.nbHeuresTravPersonnelGroupement12m, 1000000);
        this.securiteChantiers.tauxFrequenceInterimaires = this.divide(this.securiteChantiers.nbAccidentAvecArretInterimaires, this.securiteChantiers.nbHeuresTravInterimaires, 1000000);
        this.securiteChantiers.tauxFrequenceInterimaires12m = this.divide(this.securiteChantiers.nbAccidentAvecArretInterimaires12m, this.securiteChantiers.nbHeuresTravInterimaires12m, 1000000);
        this.securiteChantiers.tauxFrequenceSousTraitants = this.divide(this.securiteChantiers.nbAccidentAvecArretSousTraitants, this.securiteChantiers.nbHeuresTravSousTraitants, 1000000);
        this.securiteChantiers.tauxFrequenceSousTraitants12m = this.divide(this.securiteChantiers.nbAccidentAvecArretSousTraitants12m, this.securiteChantiers.nbHeuresTravSousTraitants12m, 1000000);
        this.securiteChantiers.tauxFrequenceGlobalChantier = this.divide(this.securiteChantiers.nbAccidentAvecArretGlobalChantier, this.securiteChantiers.nbHeuresTravGlobalChantier, 1000000);
        this.securiteChantiers.tauxFrequenceGlobalChantier12m = this.divide(this.securiteChantiers.nbAccidentAvecArretGlobalChantier12m, this.securiteChantiers.nbHeuresTravGlobalChantier12m, 1000000);
        this.securiteChantiers.tauxFrequenceEncadrementChantier = this.divide(this.securiteChantiers.nbAccidentAvecArretEncadrementChantier, this.securiteChantiers.nbHeuresTravEncadrementChantier, 1000000);
        this.securiteChantiers.tauxFrequenceEncadrementChantier12m = this.divide(this.securiteChantiers.nbAccidentAvecArretEncadrementChantier12m, this.securiteChantiers.nbHeuresTravEncadrementChantier12m, 1000000);
        this.securiteChantiers.tauxFrequenceGlobalChantierEncadrement = this.divide(this.securiteChantiers.nbAccidentAvecArretGlobalChantierEncadrement, this.securiteChantiers.nbHeuresTravGlobalChantierEncadrement, 1000000);
        this.securiteChantiers.tauxFrequenceGlobalChantierEncadrement12m = this.divide(this.securiteChantiers.nbAccidentAvecArretGlobalChantierEncadrement12m, this.securiteChantiers.nbHeuresTravGlobalChantierEncadrement12m, 1000000);

        this.securiteChantiers.indiceFrequencePersonnelGroupement = this.divide(this.securiteChantiers.nbAccidentAvecArretPersonnelGroupement, this.securiteChantiers.effectifPersonnelGroupement, 1000);
        this.securiteChantiers.indiceFrequencePersonnelGroupement12m = this.divide(this.securiteChantiers.nbAccidentAvecArretPersonnelGroupement12m, this.securiteChantiers.effectifPersonnelGroupement12m, 1000);
        this.securiteChantiers.indiceFrequenceInterimaires = this.divide(this.securiteChantiers.nbAccidentAvecArretInterimaires, this.securiteChantiers.effectifInterimaires, 1000);
        this.securiteChantiers.indiceFrequenceInterimaires12m = this.divide(this.securiteChantiers.nbAccidentAvecArretInterimaires12m, this.securiteChantiers.effectifInterimaires12m, 1000);
        this.securiteChantiers.indiceFrequenceSousTraitants = this.divide(this.securiteChantiers.nbAccidentAvecArretSousTraitants, this.securiteChantiers.effectifSousTraitants, 1000);
        this.securiteChantiers.indiceFrequenceSousTraitants12m = this.divide(this.securiteChantiers.nbAccidentAvecArretSousTraitants12m, this.securiteChantiers.effectifSousTraitants12m, 1000);
        this.securiteChantiers.indiceFrequenceGlobalChantier = this.divide(this.securiteChantiers.nbAccidentAvecArretGlobalChantier, this.securiteChantiers.effectifGlobalChantier, 1000);
        this.securiteChantiers.indiceFrequenceGlobalChantier12m = this.divide(this.securiteChantiers.nbAccidentAvecArretGlobalChantier12m, this.securiteChantiers.effectifGlobalChantier12m, 1000);
        this.securiteChantiers.indiceFrequenceEncadrementChantier = this.divide(this.securiteChantiers.nbAccidentAvecArretEncadrementChantier, this.securiteChantiers.effectifEncadrementChantier, 1000);
        this.securiteChantiers.indiceFrequenceEncadrementChantier12m = this.divide(this.securiteChantiers.nbAccidentAvecArretEncadrementChantier12m, this.securiteChantiers.effectifEncadrementChantier12m, 1000);
        this.securiteChantiers.indiceFrequenceGlobalChantierEncadrement = this.divide(this.securiteChantiers.nbAccidentAvecArretGlobalChantierEncadrement, this.securiteChantiers.effectifGlobalChantierEncadrement, 1000);
        this.securiteChantiers.indiceFrequenceGlobalChantierEncadrement12m = this.divide(this.securiteChantiers.nbAccidentAvecArretGlobalChantierEncadrement12m, this.securiteChantiers.effectifGlobalChantierEncadrement12m, 1000);

        this.securiteChantiers.tauxGravitePersonnelGroupement = this.divide(this.securiteChantiers.nbJourArretPersonnelGroupement, this.securiteChantiers.nbHeuresTravPersonnelGroupement, 1000);
        this.securiteChantiers.tauxGravitePersonnelGroupement12m = this.divide(this.securiteChantiers.nbJourArretPersonnelGroupement12m, this.securiteChantiers.nbHeuresTravPersonnelGroupement12m, 1000);
        this.securiteChantiers.tauxGraviteInterimaires = this.divide(this.securiteChantiers.nbJourArretInterimaires, this.securiteChantiers.nbHeuresTravInterimaires, 1000);
        this.securiteChantiers.tauxGraviteInterimaires12m = this.divide(this.securiteChantiers.nbJourArretInterimaires12m, this.securiteChantiers.nbHeuresTravInterimaires12m, 1000);
        this.securiteChantiers.tauxGraviteSousTraitants = this.divide(this.securiteChantiers.nbJourArretSousTraitants, this.securiteChantiers.nbHeuresTravSousTraitants, 1000);
        this.securiteChantiers.tauxGraviteSousTraitants12m = this.divide(this.securiteChantiers.nbJourArretSousTraitants12m, this.securiteChantiers.nbHeuresTravSousTraitants12m, 1000);
        this.securiteChantiers.tauxGraviteGlobalChantier = this.divide(this.securiteChantiers.nbJourArretGlobalChantier, this.securiteChantiers.nbHeuresTravGlobalChantier, 1000);
        this.securiteChantiers.tauxGraviteGlobalChantier12m = this.divide(this.securiteChantiers.nbJourArretGlobalChantier12m, this.securiteChantiers.nbHeuresTravGlobalChantier12m, 1000);
        this.securiteChantiers.tauxGraviteEncadrementChantier = this.divide(this.securiteChantiers.nbJourArretEncadrementChantier, this.securiteChantiers.nbHeuresTravEncadrementChantier, 1000);
        this.securiteChantiers.tauxGraviteEncadrementChantier12m = this.divide(this.securiteChantiers.nbJourArretEncadrementChantier12m, this.securiteChantiers.nbHeuresTravEncadrementChantier12m, 1000);
        this.securiteChantiers.tauxGraviteGlobalChantierEncadrement = this.divide(this.securiteChantiers.nbJourArretGlobalChantierEncadrement, this.securiteChantiers.nbHeuresTravGlobalChantierEncadrement, 1000);
        this.securiteChantiers.tauxGraviteGlobalChantierEncadrement12m = this.divide(this.securiteChantiers.nbJourArretGlobalChantierEncadrement12m, this.securiteChantiers.nbHeuresTravGlobalChantierEncadrement12m, 1000);
      },
      sum(fields) {
        return sum(fields);
      },
      divide(field1, field2, times) {
        return divide(field1, field2, times);
      },

      onSubmit(evt) {
        this.isMissingMonths = false;
        this.missingMonths = ''
        evt.preventDefault();
        if (!this.isDateFilled) {
          AlertStore.pushError('Mois non renseigné');
          return;
        }
        this.securiteChantiers.date = momentToDate(this.date);
        if (this.mode === 'CREATE') {
          this.securiteChantiers.marche = this.isAdmin ? this.marches.find(marche => marche.id === this.marcheAdmin) : this.sessionStoreState.marche;
          const marcheId = this.isAdmin ? this.marcheAdmin : this.sessionStoreState.marche.id;
          MarcheSecuriteChantiersStore.getMissingMonths(marcheId, momentToDate(this.date)).then(value => {
            this.isMissingMonths = true;
            value.forEach((element) => {
              this.missingMonths = this.missingMonths + moisIndexe[moment(element).format("M")]+ '-'+ moment(element).format("YYYY") + ' ';
            });
            MarcheSecuriteChantiersStore.create(this.securiteChantiers);
          });
        } else {
          MarcheSecuriteChantiersStore.update(this.securiteChantiers);
        }
      },
      onReset(evt) {
        evt.preventDefault();
        MarcheSecuriteChantiersStore.clearLocalStorage();
        this.initSecuriteChantiers();
      },
      validerSecuriteChantiers(securiteChantiers) {
        MarcheSecuriteChantiersStore.validate(securiteChantiers).then(() => {
          this.$router.push("/marche-securite-chantiers/validation");
        });
      },
      getLocalStorage() {
        if(localStorage.previousSecuriteChantiers && localStorage.previousSecuriteChantiers!=="{}"){
          this.previousSecuriteChantiers = JSON.parse(localStorage.previousSecuriteChantiers);
        }
        if ((localStorage.securiteChantiers && localStorage.securiteChantiers !== "{}") || string2literal(localStorage.dateSecuriteChantiers) || string2literal(localStorage.marcheAdminSecuriteChantiers)) {
          if (localStorage.securiteChantiers && localStorage.securiteChantiers !== "{}") {
            Object.assign(this.securiteChantiers, JSON.parse(localStorage.securiteChantiers));
            this.securiteChantiers.id = null;
          }
          this.date = moment(new Date(localStorage.dateSecuriteChantiers));
          this.marcheAdmin = parseInt(localStorage.marcheAdminSecuriteChantiers);
          AlertStore.pushSuccess(libelles.messageLocalStorage);
        } else {
          this.securiteChantiers = new MarcheSecuriteChantiersDto();
        }
      },
      isValueZero(fieldMois, fieldNbHeures, doubleCheck) {
        if (doubleCheck) {
          return (Number(this.securiteChantiers[fieldMois]) === 0 && Number(this.securiteChantiers[fieldNbHeures]) !== 0) || (Number(this.securiteChantiers[fieldNbHeures]) === 0 && Number(this.securiteChantiers[fieldMois]) !== 0)
          }
        return (Number(this.securiteChantiers[fieldNbHeures]) === 0 && Number(this.securiteChantiers[fieldMois]) !== 0)
      },

      isValueZeroField(fieldMois, fieldNbHeures, doubleCheck) {
        var result = this.securiteChantiers[fieldMois]  && this.securiteChantiers[fieldNbHeures] && this.isValueZero(fieldMois, fieldNbHeures, doubleCheck);
        this.invalidFormNbHeures = result? true: false;
        return result;
      },

      check12M(field, field12M){
        return (Number(this.securiteChantiers[field]) >  Number(this.securiteChantiers[field12M]))?true:false; 
      },

      check12MField(fieldA, fieldB){
        if(this.securiteChantiers[fieldA]  && this.securiteChantiers[fieldB] && this.check12M(fieldA,fieldB)){
          this.invalidForm12M = true;
          return false;
        }
        this.invalidForm12M = false;
        return null;
      },

      onMarcheSearch(marche, isId) {
        this.marcheIdsItems = MarcheStore.onMarcheSearch(marche, isId, this.marchesAdmin)
      },
      isBeforeCurrentDate() {
        var currentdate = new Date();
        if (this.date) {
          return momentToDate(this.date) < currentdate;
        }
      },
      saveReport() {
        //Effectif 12 M
        this.securiteChantiers["effectifPersonnelGroupement12m"] = this.previousSecuriteChantiers[0].effectifPersonnelGroupement12m;
        this.securiteChantiers["effectifInterimaires12m"]=this.previousSecuriteChantiers[0].effectifInterimaires12m;
        this.securiteChantiers["effectifSousTraitants12m"]=this.previousSecuriteChantiers[0].effectifSousTraitants12m;
        this.securiteChantiers["effectifEncadrementChantier12m"]=this.previousSecuriteChantiers[0].effectifEncadrementChantier12m;
        //Effectif et nb heures
        this.securiteChantiers["effectifPersonnelGroupement"]=0;
        this.securiteChantiers["nbHeuresTravPersonnelGroupement"]=0;
        this.securiteChantiers["effectifInterimaires"]=0;
        this.securiteChantiers["nbHeuresTravInterimaires"]=0;
        this.securiteChantiers["effectifSousTraitants"]=0;
        this.securiteChantiers["nbHeuresTravSousTraitants"]=0;
        this.securiteChantiers["effectifEncadrementChantier"]=0;
        this.securiteChantiers["nbHeuresTravEncadrementChantier"]=0;
        // NB heures 12 m
        this.update12m(0, 'nbHeuresTravPersonnelGroupement');
        this.update12m(0, 'nbHeuresTravInterimaires');
        this.update12m(0, 'nbHeuresTravSousTraitants');
        this.update12m(0, 'nbHeuresTravEncadrementChantier');
        //AT
        this.securiteChantiers["nbAccidentSansArretPersonnelGroupement"]=0;
        this.securiteChantiers["nbAccidentAvecArretPersonnelGroupement"]=0;
        this.securiteChantiers["nbJourArretPersonnelGroupement"]=0;
        this.securiteChantiers["nbAccidentSansArretInterimaires"]=0;
        this.securiteChantiers["nbAccidentAvecArretInterimaires"]=0;
        this.securiteChantiers["nbJourArretInterimaires"]=0;
        this.securiteChantiers["nbAccidentSansArretSousTraitants"]=0;
        this.securiteChantiers["nbAccidentAvecArretSousTraitants"]=0;
        this.securiteChantiers["nbJourArretSousTraitants"]=0;
        this.securiteChantiers["nbAccidentSansArretEncadrementChantier"]=0;
        this.securiteChantiers["nbAccidentAvecArretEncadrementChantier"]=0;
        this.securiteChantiers["nbJourArretEncadrementChantier"]=0;
        //AT 12M
        this.update12m(0, 'nbAccidentSansArretPersonnelGroupement');
        this.update12m(0, 'nbAccidentAvecArretPersonnelGroupement');
        this.update12m(0, 'nbJourArretPersonnelGroupement');
        this.update12m(0, 'nbAccidentSansArretInterimaires');
        this.update12m(0, 'nbAccidentAvecArretInterimaires');
        this.update12m(0, 'nbJourArretInterimaires');
        this.update12m(0, 'nbAccidentSansArretSousTraitants');
        this.update12m(0, 'nbAccidentAvecArretSousTraitants');
        this.update12m(0, 'nbJourArretSousTraitants');
        this.update12m(0, 'nbAccidentSansArretEncadrementChantier');
        this.update12m(0, 'nbAccidentAvecArretEncadrementChantier');
        this.update12m(0, 'nbJourArretEncadrementChantier');
        // Taux et indices
        this.securiteChantiers["tauxFrequencePersonnelGroupement"]=0;
        this.securiteChantiers["tauxFrequenceInterimaires"]=0;
        this.securiteChantiers["tauxFrequenceSousTraitants"]=0;
        this.securiteChantiers["tauxFrequenceGlobalChantier"]=0;
        this.securiteChantiers["tauxFrequenceEncadrementChantier"]=0;
        this.securiteChantiers["tauxFrequenceGlobalChantierEncadrement"]=0;
        this.securiteChantiers["indiceFrequencePersonnelGroupement"]=0;
        this.securiteChantiers["indiceFrequenceInterimaires"]=0;
        this.securiteChantiers["indiceFrequenceSousTraitants"]=0;
        this.securiteChantiers["indiceFrequenceGlobalChantier"]=0;
        this.securiteChantiers["indiceFrequenceEncadrementChantier"]=0;
        this.securiteChantiers["indiceFrequenceGlobalChantierEncadrement"]=0;
        this.securiteChantiers["tauxGravitePersonnelGroupement"]=0;
        this.securiteChantiers["tauxGraviteInterimaires"]=0;
        this.securiteChantiers["tauxGraviteSousTraitants"]=0;
        this.securiteChantiers["tauxGraviteGlobalChantier"]=0;
        this.securiteChantiers["tauxGraviteEncadrementChantier"]=0;
        this.securiteChantiers["tauxGraviteGlobalChantierEncadrement"]=0;
      }
    }
  }
</script>
<style>
    .user-info {
        background-color: #f0f0f0;
        padding: 10px;
        border: 1px solid #ccc;
        display: flex;
        flex-direction: column;
        max-width: 100%;
        box-sizing: border-box;
        word-break: break-word;
    }
    .no-data {
        text-align: center;
        font-style: italic;
        color: #888;
    }
</style>
